import { useEffect, useRef, useState } from "react";
import { Mic, MicOff } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const SR = typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition : null;

export default function MicButton({ onText, disabled, className, testId = "mic-button" }) {
    const [listening, setListening] = useState(false);
    const recRef = useRef(null);
    const emittedRef = useRef(0);

    useEffect(() => () => recRef.current?.stop(), []);

    if (!SR) return null;

    function toggle() {
        if (listening) {
            recRef.current?.stop();
            return;
        }
        const rec = new SR();
        rec.lang = "fr-FR";
        rec.interimResults = false;
        rec.continuous = true;
        emittedRef.current = 0; // nouvelle session de dictée
        rec.onresult = (e) => {
            // Reconstruit l'intégralité du transcript FINAL de la session puis n'émet QUE la partie inédite.
            // Corrige l'écrasement/duplication quand le moteur re-livre tous les résultats (resultIndex remis à 0).
            const finals = [];
            for (let i = 0; i < e.results.length; i++) if (e.results[i].isFinal) finals.push(e.results[i][0].transcript.trim());
            const full = finals.join(" ").replace(/\s+/g, " ").trim();
            if (full.length > emittedRef.current) {
                const delta = full.slice(emittedRef.current).trim();
                emittedRef.current = full.length;
                if (delta) onText(delta);
            }
        };
        rec.onerror = (e) => {
            if (e.error !== "aborted") toast.error("Dictée vocale", { description: e.error === "not-allowed" ? "Accès au microphone refusé." : `Erreur : ${e.error}` });
        };
        rec.onend = () => setListening(false);
        recRef.current = rec;
        rec.start();
        setListening(true);
    }

    return (
        <button
            type="button"
            data-testid={testId}
            onClick={toggle}
            disabled={disabled}
            title={listening ? "Arrêter la dictée" : "Dicter (reconnaissance vocale)"}
            aria-pressed={listening}
            className={cn(
                "h-9 w-9 shrink-0 rounded-full flex items-center justify-center border transition-colors disabled:opacity-40",
                listening ? "border-rose-400 bg-rose-50 text-rose-600 animate-pulse" : "border-border text-muted-foreground hover:text-primary hover:bg-accent",
                className
            )}
        >
            {listening ? <MicOff className="h-4 w-4" strokeWidth={1.75} /> : <Mic className="h-4 w-4" strokeWidth={1.75} />}
        </button>
    );
}
